import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { UsersDeleteModal } from 'pages/Users/components/UsersDeleteModal';
import { UsersMerchantAccessDialog } from 'pages/Users/components/UsersMerchantAccessDialog';
import { UsersListBulkActionsToolbar } from 'pages/Users/UsersList/UsersListBulkActionsToolbar';
import { UsersListCorporateEmailField } from 'pages/Users/UsersList/UsersListCorporateEmailField';
import { TextField, useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { UserRoleChip } from 'shared/mui/Chips';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
  ContextMenuUserMerchantAccessButton,
} from 'shared/mui/ContextMenu';
import { DateRangePickerValue } from 'shared/mui/DatePicker/DateRangePicker/DateRangePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  BooleanField,
  DateTimeField,
  FunctionField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { Permission } from 'types/embedded/permission';
import { User } from 'types/embedded/user';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { DatagridStyled } from './UsersListContent.styled';
import { UsersListFilter, UsersListFilterFormValues } from './UsersListFilter/UsersListFilter';
import { UsersListFilterChips } from './UsersListFilter/UsersListFilterChips';
import { UsersListMerchantAccessField } from './UsersListMerchantAccessField';

const FILTER_RESET_VALUES = {
  appliedDate: [null, null] as [DateRangePickerValue, DateRangePickerValue],
  lastName: '',
  firstName: '',
  roleEntities: [],
  email: '',
  enabled: null,
  reddyNumber: '',
  merchants: [],
};

export const UsersListContent: React.FC = () => {
  const { filterValues } = useListContext();
  const isListFilterExist = Object.keys(filterValues).length > 0;
  const {
    record: deleteRecord,
    handleOpenRecordModal: handleOpenDeleteModal,
    openRecordModal: openDeleteModal,
    handleCloseRecordModal: handleCloseDeleteModal,
  } = useRecordModal<User>();
  const {
    record: merchantAccessRecord,
    handleOpenRecordModal: handleOpenMerchantAccessModal,
    openRecordModal: openMerchantAccessModal,
    handleCloseRecordModal: handleCloseMerchantAccessModal,
  } = useRecordModal<User>();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<UsersListFilterFormValues>({
      defaultValues: {
        appliedDate: [
          formatInputToTimestamp(filterValues['lastActivity[gte]']),
          formatInputToTimestamp(filterValues['lastActivity[lte]']),
        ],
        lastName: filterValues.lastName || null,
        firstName: filterValues.firstName || null,
        roleEntities: filterValues.roleEntities || [],
        email: filterValues.email || null,
        enabled: filterValues.enabled || null,
        reddyNumber: filterValues.reddyNumber || null,
        merchants: filterValues['merchantAccesses.merchant'] || [],
      },
      resetValues: FILTER_RESET_VALUES,
    });

  useSyncFilter<UsersListFilterFormValues>({
    appliedFilters,
    transform: (appliedFilters) => {
      const { appliedDate = [null, null], merchants, ...restFilters } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        'lastActivity[gte]': formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        'lastActivity[lte]': formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        'merchantAccesses.merchant': merchants,
      });
    },
  });

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[<FilterButton key="filter" onClick={toggleDrawer(true)} />]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <UsersListFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <UsersListFilterChips />
            <FilterClearButton key="clear-button" onClick={onReset} visible={isListFilterExist} />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <Box
        sx={{
          minHeight: '100%',
          display: 'initial',
        }}
      >
        <DatagridStickyContainer>
          <DatagridStyled
            bulkActionsToolbar={
              hasResourcePermissions(Resources.Users, [Permission.Full, Permission.Update]) ? (
                <UsersListBulkActionsToolbar />
              ) : undefined
            }
          >
            <TextField label="ID" sortBy="id" source="originId" />
            <FunctionField
              label="Пользователь"
              render={() => <UserProfileField showLink />}
              source="firstName"
            />
            <BooleanField label="Активен" source="enabled" />
            <FunctionField
              label="Роль"
              render={(record: User) => {
                if (!record?.rolesValues?.length) {
                  return EMPTY_FIELD;
                }
                return (
                  <Box display="flex" flexWrap="wrap" gap="4px">
                    {record.rolesValues.map((val, index) => (
                      <UserRoleChip key={index} roleValue={val} />
                    ))}
                  </Box>
                );
              }}
              sortable={false}
            />
            <UsersListMerchantAccessField
              label="Доступ к мерчантам"
              sortable={false}
              source="merchantAccesses"
            />
            <DateTimeField label="Последняя активность" source="lastActivity" />
            <TextField emptyText={EMPTY_FIELD} label="Reddy" source="reddyNumber" />
            <FunctionField
              label="Групповой Email"
              render={() => <UsersListCorporateEmailField />}
              source="corporateEmail"
            />
            <DateTimeField label="Дата создания" source="createdAt" />
            <DateTimeField label="Дата редактир." source="updatedAt" />
            <ContextMenu
              render={(record: User) => [
                hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton entityName="user" key="user-actions-button" />
                ),
                hasResourcePermissions('merchant_accesses', [
                  Permission.Full,
                  Permission.Update,
                ]) && (
                  <ContextMenuUserMerchantAccessButton
                    key="merchant-access-button"
                    onClick={handleOpenMerchantAccessModal}
                  />
                ),
                <ContextMenuShowButton key="show_button" />,
                hasResourcePermissions(Resources.Users, [Permission.Full, Permission.Update]) && (
                  <ContextMenuEditButton key="edit-button" />
                ),
                hasResourcePermissions(Resources.Users, [Permission.Full, Permission.Delete]) && (
                  <ContextMenuDeleteButton
                    key="delete-button"
                    onClick={() => handleOpenDeleteModal(record)}
                  />
                ),
              ]}
            />
          </DatagridStyled>
        </DatagridStickyContainer>
      </Box>
      {openDeleteModal && (
        <UsersDeleteModal
          handleCloseRecordModal={handleCloseDeleteModal}
          openRecordModal={openDeleteModal}
          record={deleteRecord}
        />
      )}
      {openMerchantAccessModal && (
        <UsersMerchantAccessDialog
          onClose={handleCloseMerchantAccessModal}
          open={openMerchantAccessModal}
          record={merchantAccessRecord}
        />
      )}
    </>
  );
};
